import { getLocalTimeZone } from '@internationalized/date';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { HelpCircleIcon, PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import AccountGroup from '../../components/AccountGroup';
import AccountRow from '../../components/AccountRow';
import DeleteFinancialAccountModal from '../../components/DeleteFinancialAccountModal';
import Entity from '../../components/entity';
import PlaidLinkButton from '../../components/plaid/PlaidLinkButton';
import Widget from '../../components/Widget';
import { useFinancialAccounts, type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useInvestmentBalancesAt } from '../../hooks/queries/useInvestmentBalancesAt';
import { useFeatureGates } from '../../hooks/useFeatureGates';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { PATHS } from '../../paths';
import { useNavigateWithDateRange } from '../../providers/DateRangeProvider';
import { filterByColumn } from '../../providers/TransactionsProvider';
import { isSupportedFinancialAccount } from '../../utils/account/accounts';
import { ACCOUNT_SECTIONS, groupAccountsBySection, type AccountSection } from '../../utils/account/accountSections';
import { startOfDayDaysAgo } from '../../utils/account/balanceHistory';

import type { ColumnTable } from 'arquero';

const LAST_MONTH_DAYS = 30;

interface IBaseAccountOverviewPaneProps {
  financialAccounts: FinancialAccount[];
  unsupportedFinancialAccounts: FinancialAccount[];
  includeInvestments: boolean;
  /** Each investment account's value a month ago, by account id. */
  investmentPastBalances: ReadonlyMap<string, number | null>;
  transactions: ColumnTable | null;
  onAccountClick: (accountId: string) => void;
  loading: boolean;
}

export const BaseAccountOverviewPane = ({
  financialAccounts,
  unsupportedFinancialAccounts,
  includeInvestments,
  investmentPastBalances,
  transactions,
  onAccountClick,
  loading,
}: IBaseAccountOverviewPaneProps) => {
  const [accountToHide, setAccountToHide] = React.useState<FinancialAccount | null>(null);
  const groupedFinancialAccounts = React.useMemo(
    () => groupAccountsBySection(financialAccounts, { includeInvestments }),
    [financialAccounts, includeInvestments]
  );

  const transactionsBySection = React.useMemo(() => {
    const result: Partial<Record<AccountSection, ColumnTable>> = {};
    if (!transactions) {
      return result;
    }

    for (const section of ACCOUNT_SECTIONS) {
      const accountIds = groupedFinancialAccounts[section].map((account) => account.id);
      if (accountIds.length > 0) {
        result[section] = filterByColumn(transactions, 'financialAccountId', accountIds);
      }
    }

    return result;
  }, [transactions, groupedFinancialAccounts]);

  const addButton = (
    <PlaidLinkButton aria-label="Add account" iconLeading={<PlusIcon className="text-button-primary-icon size-5" />} />
  );

  const accountsBody = (
    <div className="flex flex-col gap-6">
      {ACCOUNT_SECTIONS.map((section) => {
        const accounts = groupedFinancialAccounts[section];
        if (accounts.length === 0) {
          return null;
        }

        return (
          <AccountGroup
            key={section}
            section={section}
            accounts={accounts}
            transactions={transactionsBySection[section]}
            pastBalances={section === 'investments' ? investmentPastBalances : undefined}
            onAccountClick={onAccountClick}
            // The other sections' accounts are hidden from their own account page.
            onHideAccount={section === 'investments' ? setAccountToHide : undefined}
          />
        );
      })}
      {unsupportedFinancialAccounts.length > 0 && (
        <Widget collapsible defaultCollapsed>
          <Widget.Header
            title={
              <Tooltip title="Connected accounts that Mantle does not currently support.">
                <div className="flex items-center gap-2">
                  Unsupported accounts
                  <TooltipTrigger aria-label="About unsupported accounts">
                    <HelpCircleIcon className="text-secondary size-4" />
                  </TooltipTrigger>
                </div>
              </Tooltip>
            }
          />
          <Widget.Body>
            {unsupportedFinancialAccounts.map((financialAccount) => (
              <AccountRow
                key={financialAccount.id}
                financialAccount={financialAccount}
                hideTrailing
                onHide={() => setAccountToHide(financialAccount)}
              />
            ))}
          </Widget.Body>
        </Widget>
      )}
      {accountToHide && (
        <DeleteFinancialAccountModal
          account={accountToHide}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setAccountToHide(null);
            }
          }}
        />
      )}
    </div>
  );

  return (
    <Entity.Pane>
      <Entity.Header title="Accounts" trailingContent={addButton} />
      <Entity.Body
        primaryElement={
          <>
            {loading && <LoadingIndicator fullScreen className="h-[500px]" />}
            {!loading && financialAccounts.length === 0 && <div>No financial accounts found</div>}
            {!loading && financialAccounts.length > 0 && accountsBody}
          </>
        }
      />
    </Entity.Pane>
  );
};

const AccountOverviewPane = () => {
  const navigate = useNavigateWithDateRange();

  const { transactions, loading: transactionsLoading } = useFilteredTransactions({
    includePaired: true,
  });
  const { financialAccounts, loading } = useFinancialAccounts();

  const { flags, loading: gatesLoading, resolved: gatesResolved } = useFeatureGates();
  const includeInvestments = flags.has(FeatureGate.InvestmentAccounts);
  // Until a gate set has been read the pane stays loading, so a first load never draws an investment
  // account under "Unsupported accounts" and then moves it. A cached set, empty or not, is trusted.
  const gatesUnknown = gatesLoading && !gatesResolved;

  const [lastMonth] = React.useState(() => startOfDayDaysAgo(new Date(), getLocalTimeZone(), LAST_MONTH_DAYS));
  const investmentPastBalances = useInvestmentBalancesAt(lastMonth, { skip: !includeInvestments });

  const isSupported = (account: FinancialAccount) => isSupportedFinancialAccount(account, { includeInvestments });
  const filteredFinancialAccounts = financialAccounts?.filter(isSupported) ?? [];
  const unsupportedFinancialAccounts = financialAccounts?.filter((account) => !isSupported(account)) ?? [];

  const handleAccountClick = (accountId: string) => navigate(PATHS.ACCOUNT(accountId));

  return (
    <BaseAccountOverviewPane
      financialAccounts={filteredFinancialAccounts}
      unsupportedFinancialAccounts={unsupportedFinancialAccounts}
      includeInvestments={includeInvestments}
      investmentPastBalances={investmentPastBalances}
      transactions={transactions ?? null}
      onAccountClick={handleAccountClick}
      loading={loading || transactionsLoading || gatesUnknown}
    />
  );
};

export default AccountOverviewPane;
